/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  CheckmarkOutline16,
  ChevronDown16,
  ChevronRight16,
  Sunny16,
  TreeView16,
  WarningAltFilled16,
} from '@carbon/icons-react';
import { Divider as AntdDivider } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import tw from 'tailwind-styled-components';
import { Company } from 'api/models';
import { ProjectPhase } from 'api/project';
import { PinScopeType, PinResourceType, Spotlight } from 'api/spotlight/types';
import {
  FieldOption,
  Field,
  StatusGroup,
  Task,
  TaskAPI,
} from 'api/tasks/index';
import { ReactComponent as SpotligightSvg } from 'assets/svgs/spotlight.svg';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import {
  CpBody00Placeholder,
  CpBody00Primary,
  CpBody00Secondary,
  CpBody01Placeholder,
  CpBody01Primary,
  CpBody03Placeholder,
  CpHeading03Primary,
} from 'components/atoms/customer-portal/typography';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { SpotlightForTaskDrawer } from 'components/organisms/spotlight/spotlight-for-task';
import { useSelectedStatusOption } from 'data/fields/hooks';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import { useGetPinsByScopeAndResourceType } from 'hooks/use-spotlight';
import { ArrElement } from 'utils/arrays';
import { getRandomColor } from 'utils/get-random-color';
import { CpMenu } from '../components/modal/cp-menu';
import { CpPopover } from '../components/modal/cp-popover';
import { CpProgressBar } from '../components/progress/progress';
import {
  CpStatusPicker,
  CpStatusPickerProps,
} from '../components/status-picker/cp-status-picker';
import { CpTag } from '../components/tags/cp-tags';
import { useGetBackground } from '../types/utils';
import { CpWidgetProps } from '../types/widget';
import {
  ChevronWrapper,
  CompletedTag,
  CpSectionWrapper,
  MarkasInProgress,
} from './common';
import { CpTaskCardAssignees } from './cp-task-card-assignees';

enum ProjectPlanTaskType {
  IndividualTask = 'INDIVIDUAL_TASK',
  CompletedGroupedTasks = 'COMPLETED_GROUPED_TASKS',
}

type ProjectPlanTaskRow = {
  task: Task;
  tasks?: Task[];
  taskType: ProjectPlanTaskType;
};

interface CpProjectPlanRenderInput extends Record<string, unknown> {
  title: string;
  description: string;
  projectPhases: {
    projectPhase: ProjectPhase;
    tasks: Task[];
    taskCount: number;
    completedTasksCount: number;
    projectPlanTaskRows: ProjectPlanTaskRow[];
  }[];
  vendor: Company;
  customer: Company;
  statusField: Field;
}

type DateFilterType = 'OVERDUE' | 'THIS_WEEK' | 'NEXT_WEEK';
const dateFilters: (DateFilterType | 'SPOTLIGHT' | 'PLAN')[] = [
  'OVERDUE',
  'THIS_WEEK',
  'NEXT_WEEK',
];

const FilterBar = tw.div`
  cp-flex
  cp-items-center
  cp-justify-between
  cp-my-[var(--cp-spacing-06)]
  md:cp-flex-row
  cp-flex-col
`;

const Divider = styled(AntdDivider)`
  min-width: 0 !important;
  width: calc(100% - 16px);
  margin: 0 auto;
  transform: translateY(-1px);
  border-top: 1px solid var(--cp-border-subtle);
`;

const Tag = styled(CpTag)`
  margin-right: var(--cp-spacing-03);
`;

const StyledPopover = styled(CpPopover)``;

const FilterSetWrapper = tw.div`
  cp-w-full
  cp-flex
  cp-items-center
  cp-justify-start
`;

const CompanyFilterSetWrapper = FilterSetWrapper;

const PhaseWrapper = styled.div.attrs<{
  className?: string;
  isExpanded?: boolean;
}>((props) => ({
  className: `cp-bg-[var(--cp-coolest-gray-10)] cp-px-[var(--cp-spacing-05)] cp-pt-[var(--cp-spacing-07)] cp-pb-[var(--cp-spacing-09)]  cp-mb-[var(--cp-spacing-06)] cp-cursor-pointer cp-rounded-lg ${
    props?.className ?? ''
  }`,
}))<{ className?: string; isExpanded?: boolean }>`
  padding-bottom: ${(props) =>
    props.isExpanded ? 'var(--cp-spacing-09)' : 'var(--cp-spacing-07)'};
  transition: box-shadow 0.3s ease-in-out;
  :hover {
    box-shadow: var(--shadow-12);
  }
  @media (max-width: 480px) {
    padding-left: var(--cp-spacing-03);
    padding-right: var(--cp-spacing-03);
    padding-top: var(--cp-spacing-05);
    padding-bottom: var(--cp-spacing-05);
    margin-bottom: var(--cp-spacing-03);
  }

  @media (max-width: 768px) {
    margin-bottom: var(--cp-spacing-05);
  }
`;

const TaskWrapper = styled.div.attrs((props) => ({
  className: `rl-cp-project-plan-task cp-flex cp-items-center cp-justify-start cp-cursor-pointer cp-w-full cp-h-full lg:cp-flex-row cp-flex-col-reverse cp-overflow-x-hidden cp-py-[var(--cp-spacing-05)] cp-px-[var(--cp-spacing-05)] ${props?.className}`,
}))<{ className?: string }>`
  /* border-bottom: 1px solid var(--border-color); */
  :hover {
    border-radius: var(--border-radius-default);
    background: var(--cp-hover-default-gray);
    ${Divider} {
      display: none;
    }
  }
`;

const StatusWrapper = tw.div`
  rl-cp-project-plan-task-status
  cp-h-full
  cp-inline-flex
  cp-items-center
  cp-justify-center
  cp-mb-0
  `;

const TaskNameAndDescWrapper = styled.div.attrs({
  className:
    'rl-cp-project-plan-task-name-and-desc cp-inline-flex cp-flex-col cp-w-full lg:cp-max-w-[80%] cp-text-ellipsis cp-flex-wrap',
})``;

const TaskName = styled(CpBody01Primary)`
  margin-bottom: 0;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  svg {
    margin-right: var(--cp-spacing-03);
  }
`;

const CompletedTaskName = styled(CpBody01Placeholder)`
  margin-bottom: 0;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  svg {
    margin-right: var(--cp-spacing-03);
  }
`;

const TaskDesc = tw.div`
rl-cp-project-plan-task-desc
cp-mb-0
cp-mt-[var(--cp-spacing-03)]
cp-flex
cp-items-center
cp-justify-start
cp-text-[var(--gray-50)]
cp-font-para-small
`;

const DueDate = tw(CpBody00Secondary)`
  rl-cp-project-plan-task-date
  cp-mb-0
`;

const StatusLabel = styled(CpBody00Secondary)`
  margin-bottom: 0;
  margin-left: var(--cp-spacing-02);
`;

const StyledMenu = styled(CpMenu)`
  overflow: hidden;
`;

const StatusPickerWrapper = styled.div.attrs<{ className?: string }>(
  (props) => ({
    className: `rl-cp-project-plan-task-status-picker-wrapper cp-flex cp-items-center cp-justify-end cp-h-full ${props?.className}`,
  }),
)<{ className?: string }>`
  width: --webkit-fill-available;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TaskRowItem = ({
  data: { task: _data, spotlight, type, statusField },
  refetch,
}: {
  data: {
    task: Task;
    spotlight?: Spotlight;
    type: ProjectPlanTaskType;
    statusField: Field;
  };
  refetch: () => void;
}) => {
  const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();
  const data: Task = useMemo(() => _data, [_data]);
  const fields = useMemo(() => data?.fields, [data?.fields]);
  const selectedStatusOption = useSelectedStatusOption(fields || []);

  const dueDate = useMemo(() => data?.dueDate, [data?.dueDate]);
  // const startDate = useMemo(() => data?.startDate, [data?.startDate]);
  const date = useMemo(() => {
    // && !startDate
    if (!dueDate) {
      return '';
    }
    // if (!dueDate) {
    //   return moment(startDate).format('DD MMM YY');
    // }
    // if (!startDate) {
    //   return moment(dueDate).format('DD MMM YY');
    // }

    // dont display year if it is current year
    if (moment(dueDate).year() === moment().year()) {
      return moment(dueDate).format('DD MMM');
    }

    return moment(dueDate).format('DD MMM YY');
  }, [dueDate]);

  const onStatusChange = useCallback(
    (change: Required<FieldOption<StatusGroup>>): void => {
      TaskAPI.updateTask(data?.project?.projectId, data?.taskId, {
        taskId: data?.taskId,
        fields: [
          {
            fieldId: statusField?.fieldId,
            fieldValue: change?.value,
          },
        ],
      }).then(refetch);
    },
    [data?.project?.projectId, data?.taskId, refetch, statusField?.fieldId],
  );

  const onMarkAsInProgressOrCompleted = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (selectedStatusOption) {
        if (selectedStatusOption?.group === StatusGroup.InProgress) {
          onStatusChange({
            ...selectedStatusOption,

            value: 3,
          });
        } else {
          onStatusChange({
            ...selectedStatusOption,
            value: 2,
          });
        }
      }
    },
    [onStatusChange, selectedStatusOption],
  );

  const onTaskClick = useCallback(() => {
    openTaskDrawer(data?.project?.projectId, data?.taskId);
  }, [data?.project?.projectId, data?.taskId, openTaskDrawer]);

  if (!data) {
    return <></>;
  }

  const Name =
    type === ProjectPlanTaskType.CompletedGroupedTasks
      ? CompletedTaskName
      : TaskName;

  if (type === ProjectPlanTaskType.CompletedGroupedTasks) {
    return (
      <>
        <TaskWrapper data-task-id={data?.taskId} onClick={onTaskClick}>
          <TaskNameAndDescWrapper>
            <Name className="cp-line-through">
              {selectedStatusOption ? (
                <StatusIndicator status={selectedStatusOption} />
              ) : (
                <></>
              )}

              {data?.taskName}
            </Name>
          </TaskNameAndDescWrapper>
          <StatusPickerWrapper
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {selectedStatusOption?.group === StatusGroup.Completed ? (
              <div className="cp-flex cp-items-center">
                <ChevronWrapper className="cp-invisible cp-mr-[var(--cp-spacing-07)] cp-ml-0">
                  <ChevronDown16 />
                </ChevronWrapper>
                <StyledPopover
                  placement="bottom"
                  getPopupContainer={() =>
                    document.getElementById('cp-modal-root') as HTMLElement
                  }
                  trigger={['click']}
                  content={
                    <CpStatusPicker
                      selectedOption={selectedStatusOption?.value}
                      title={<FormattedMessage id="task_status" />}
                      onChange={onStatusChange}
                      options={
                        (statusField?.metaData?.options ??
                          []) as unknown as CpStatusPickerProps['options']
                      }
                    />
                  }
                >
                  <CompletedTag color={selectedStatusOption?.color}>
                    <span className="status-label">
                      {selectedStatusOption?.label}
                    </span>
                    <ChevronDown16 />
                  </CompletedTag>
                </StyledPopover>
              </div>
            ) : (
              <div className="cp-flex cp-items-center">
                <MarkasInProgress onClick={onMarkAsInProgressOrCompleted}>
                  <span className="status-label">
                    <FormattedMessage
                      id={
                        selectedStatusOption?.group === StatusGroup.InProgress
                          ? 'customer_portal.widgets.project_plan.task_list.status_picker.mark_as_completed'
                          : 'customer_portal.widgets.project_plan.task_list.status_picker.mark_as_in_progress'
                      }
                    />
                  </span>
                </MarkasInProgress>
                <StyledPopover
                  placement="bottom"
                  trigger={['click']}
                  getPopupContainer={() =>
                    document.getElementById('cp-modal-root') as HTMLElement
                  }
                  content={
                    <CpStatusPicker
                      selectedOption={selectedStatusOption?.value}
                      title={<FormattedMessage id="task_status" />}
                      onChange={onStatusChange}
                      options={
                        (statusField?.metaData?.options ??
                          []) as unknown as CpStatusPickerProps['options']
                      }
                    />
                  }
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <ChevronWrapper>
                      <ChevronDown16 />
                    </ChevronWrapper>
                  </div>
                </StyledPopover>
              </div>
            )}
          </StatusPickerWrapper>
        </TaskWrapper>
        <Divider />
      </>
    );
  }

  return (
    <div className={spotlight ? 'cp-mt-[var(--cp-spacing-05)]' : 'cp-w-full'}>
      {spotlight ? (
        <div className="cp-mb-[var(--cp-spacing-03)] cp-w-full">
          <SpotlightForTaskDrawer spotlight={spotlight} />
        </div>
      ) : (
        <></>
      )}
      <TaskWrapper data-task-id={data?.taskId} onClick={onTaskClick}>
        <TaskNameAndDescWrapper>
          <TaskName>
            {spotlight ? <SpotligightSvg /> : <></>}
            {selectedStatusOption ? (
              <StatusIndicator status={selectedStatusOption} />
            ) : (
              <></>
            )}

            {data?.atRisk ? (
              <WarningAltFilled16 color="var(--cp-red-60)" />
            ) : (
              <></>
            )}

            {data?.taskName}
            {data?.subTasks?.length ? (
              <TreeView16 className="cp-ml-[var(--cp-spacing-02)]" />
            ) : (
              <></>
            )}
            <AvatarGroup className="cp-ml-[var(--cp-spacing-03)]" maxCount={3}>
              {data?.responsible?.map((e) => (
                <Avatar
                  size="small"
                  src={e?.companyLogoUrl}
                  alt={e?.companyName}
                  name={e?.companyName}
                />
              ))}
            </AvatarGroup>
          </TaskName>
          <TaskDesc>
            <DueDate>{date}</DueDate>
            <div className="cp-flex cp-items-center cp-justify-start cp-mx-[var(--cp-spacing-05)]">
              <StatusWrapper>
                {selectedStatusOption ? (
                  <StatusIndicator status={selectedStatusOption} />
                ) : (
                  <></>
                )}
                <StatusLabel>{selectedStatusOption?.label}</StatusLabel>
              </StatusWrapper>
            </div>
            <CpTaskCardAssignees assignee={data?.assignee} />
          </TaskDesc>
        </TaskNameAndDescWrapper>
        <StatusPickerWrapper>
          {selectedStatusOption?.group === StatusGroup.Completed ? (
            <div className="cp-flex cp-items-center">
              <ChevronWrapper className="cp-invisible cp-mr-[var(--cp-spacing-05)] cp-ml-0">
                <ChevronDown16 />
              </ChevronWrapper>
              <CompletedTag color={selectedStatusOption?.color}>
                <span className="status-label cp-body-00-primary">
                  {selectedStatusOption?.label}
                </span>{' '}
                <ChevronDown16 />{' '}
              </CompletedTag>
            </div>
          ) : (
            <div className="cp-flex cp-items-center">
              <MarkasInProgress onClick={onMarkAsInProgressOrCompleted}>
                <span className="status-label">
                  <FormattedMessage
                    id={
                      selectedStatusOption?.group === StatusGroup.InProgress
                        ? 'customer_portal.widgets.project_plan.task_list.status_picker.mark_as_completed'
                        : 'customer_portal.widgets.project_plan.task_list.status_picker.mark_as_in_progress'
                    }
                  />
                </span>
              </MarkasInProgress>
              <StyledPopover
                placement="bottom"
                trigger={['click']}
                getPopupContainer={() =>
                  document.getElementById('cp-modal-root') as HTMLElement
                }
                content={
                  <CpStatusPicker
                    selectedOption={selectedStatusOption?.value}
                    title={<FormattedMessage id="task_status" />}
                    options={
                      (statusField?.metaData?.options ??
                        []) as unknown as CpStatusPickerProps['options']
                    }
                    onChange={onStatusChange}
                  />
                }
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <ChevronWrapper>
                    <ChevronDown16 />
                  </ChevronWrapper>
                </div>
              </StyledPopover>
            </div>
          )}
        </StatusPickerWrapper>
      </TaskWrapper>
      <Divider />
    </div>
  );
};

const CompletedTaskRowItem: React.FC<{
  spotlightMap: Record<string, Spotlight>;
  tasks: Task[];
  statusField: Field;
  refetch: () => void;
}> = ({ spotlightMap, tasks, statusField, refetch }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="cp-overflow-hidden">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      {!isExpanded ? (
        <div className="cp-mb-[var(--cp-spacing-03)]">
          <div
            className="cp-flex cp-items-center cp-justify-start cp-line-through cp-text-[var(--gray-50)] cp-py-4 cp-px-3 hover:cp-bg-[var(--cp-hover-default-gray)] cp-rounded-lg"
            onClick={() => {
              setIsExpanded((p) => !p);
            }}
          >
            <span className="cp-mr-[var(--cp-spacing-02)] cp-flex cp-items-center cp-justify-center cp-py-[var(--cp-spacing-03)]">
              {isExpanded ? <ChevronDown16 /> : <ChevronRight16 />}
            </span>
            <CpBody01Placeholder className="cp-mb-0 cp-py-[var(--cp-spacing-03)]">
              <FormattedMessage
                id={
                  isExpanded
                    ? 'customer_portal.widgets.project_plan.task_list.hide_completed_tasks'
                    : 'customer_portal.widgets.project_plan.task_list.show_completed_tasks'
                }
                values={{ num: tasks?.length }}
              />
            </CpBody01Placeholder>
          </div>
          <Divider />
        </div>
      ) : (
        <></>
      )}
      {isExpanded ? (
        <div className="cp-flex cp-flex-col cp-items-center cp-justify-center">
          {tasks.map((e) => (
            <TaskRowItem
              data={{
                statusField,
                task: e,
                spotlight: spotlightMap?.[e?.taskId],
                type: ProjectPlanTaskType.CompletedGroupedTasks,
              }}
              refetch={refetch}
            />
          ))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const ShowMore = styled.div``;

const PhaseTaskListV2: React.FC<{
  rows: ProjectPlanTaskRow[];
  spotlightMap: Record<number, Spotlight>;
  defaultOpen?: boolean;
  statusField: Field;
  refetch: () => void;
}> = ({ rows, spotlightMap, statusField, refetch }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div>
      {(rows.length > 5 && !isExpanded ? rows.slice(0, 6) : rows).map((e) =>
        e?.taskType === ProjectPlanTaskType?.CompletedGroupedTasks &&
        e?.tasks &&
        e?.tasks?.length > 1 ? (
          <CompletedTaskRowItem
            spotlightMap={spotlightMap}
            tasks={(e?.tasks ?? []) as Task[]}
            statusField={statusField}
            refetch={refetch}
          />
        ) : (
          <TaskRowItem
            data={{
              task: e?.task,
              spotlight: spotlightMap?.[e?.task?.taskId],
              type: e?.taskType,
              statusField,
            }}
            refetch={refetch}
          />
        ),
      )}
      {rows.length > 5 ? (
        <ShowMore
          className="cp-flex cp-items-start cp-justify-start cp-px-[var(--cp-spacing-03)] cp-py-[var(--cp-spacing-03)] cp-text-[var(--blue-50)] cp-font-para-medium cp-cursor-pointer cp-pt-[var(--cp-spacing-05)] cp-pb-0"
          onClick={() => {
            setIsExpanded((p) => !p);
          }}
        >
          <FormattedMessage
            id={
              isExpanded
                ? 'customer_portal.widgets.project_plan.task_list.collapse_all_tasks'
                : 'customer_portal.widgets.project_plan.task_list.view_all_tasks'
            }
            values={{ num: rows?.length }}
          />
        </ShowMore>
      ) : (
        <></>
      )}
    </div>
  );
};

const ProjectPlanPhase = ({
  p,
  spotlightMap,
  statusField,
  refetch,
}: {
  p: ArrElement<CpProjectPlanRenderInput['projectPhases']>;
  spotlightMap: Record<number, Spotlight>;
  statusField: Field;
  refetch: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const CountComponent =
    p?.completedTasksCount !== p?.taskCount
      ? CpBody00Secondary
      : CpBody00Primary;

  const progress = useMemo(
    () => (p?.taskCount ? (p?.completedTasksCount / p?.taskCount) * 100 : 0),
    [p?.completedTasksCount, p?.taskCount],
  );

  return (
    <PhaseWrapper
      key={`phase_${p?.projectPhase?.projectPhaseId}`}
      isExpanded={isExpanded}
    >
      <div
        onClick={() => {
          setIsExpanded((ps) => !ps);
        }}
      >
        <div className="cp-flex cp-items-center cp-mb-[var(--cp-spacing-05)]">
          <CountComponent className="cp-flex cp-items-center cp-justify-start cp-px-[var(--cp-spacing-05)] cp-mb-0">
            <CheckmarkOutline16 className="cp-mr-[var(--cp-spacing-02)]" />
            {p?.completedTasksCount ?? '0'}/{p?.taskCount}{' '}
            <span className="cp-ml-[var(--cp-spacing-02)] cp-lowercase">
              <FormattedMessage id="tasks" />
            </span>
          </CountComponent>
          <CpProgressBar
            hideProgress
            progress={progress}
            bgColor="var(--cp-cyan-20)"
            progressColor="var(--cp-cyan-30)"
          />
        </div>
        <div className="cp-flex cp-items-center">
          <CpBody03Placeholder
            style={{
              color: `var(--${getRandomColor(
                p?.projectPhase?.projectPhaseName,
              )}-60)`,
            }}
            className="cp-mb-[var(--cp-spacing-02)] cp-px-[var(--cp-spacing-05)]"
          >
            {p?.projectPhase?.projectPhaseName}
          </CpBody03Placeholder>
        </div>
      </div>
      {isExpanded ? (
        <>
          {p?.projectPhase?.phaseDescription ? (
            <CpBody00Placeholder
              className="cp-mb-0 cp-px-[var(--cp-spacing-05)] cp-text-[var(--cp-gray-50)]"
              dangerouslySetInnerHTML={{
                // eslint-disable-next-line @typescript-eslint/naming-convention
                __html: p?.projectPhase?.phaseDescription,
              }}
            />
          ) : (
            <></>
          )}

          <PhaseTaskListV2
            statusField={statusField}
            rows={p?.projectPlanTaskRows}
            spotlightMap={spotlightMap}
            refetch={refetch}
          />
        </>
      ) : (
        <></>
      )}
    </PhaseWrapper>
  );
};

const Description = styled(CpBody03Placeholder)`
  @media (max-width: 480px) {
    flex: 1 1 auto; /* Allow the item to grow and shrink */
    overflow: hidden; /* Hide overflow text */
    white-space: normal; /* Prevent text from wrapping */
    /* ellipsize after two lines */

    display: -webkit-box;
    /* -webkit-line-clamp: 2; */
    -webkit-box-orient: vertical;
    box-orient: vertical;
    /* line-clamp: 2; */
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 768px) {
    margin-bottom: var(--cp-spacing-07);
  }
`;

const ProjectPlanTitle = styled(CpHeading03Primary)`
  margin-bottom: var(--cp-spacing-05);
  line-height: normal;

  @media (max-width: 768px) {
    margin-bottom: var(--cp-spacing-03);
  }
`;

export const CpProjectPlan: React.FC<CpWidgetProps> = ({
  disabled,
  widget,
  data: _data,
  projectId,
  runtimeConfig,
  onRuntimeConfigChange,
}) => {
  const { data: spotlights } = useGetPinsByScopeAndResourceType({
    scopeId: projectId,
    resourceType: PinResourceType.Task,
    scopeType: PinScopeType.Project,
  });

  const spotlightMap = useMemo(() => {
    return _.keyBy(spotlights, 'resourceId');
  }, [spotlights]);

  const data = useMemo(() => _data as CpProjectPlanRenderInput, [_data]);

  const phases = useMemo(
    () =>
      data?.projectPhases?.sort((a, b) => {
        const diffOfStartDate = moment(a?.projectPhase?.startDate).diff(
          moment(b?.projectPhase?.startDate),
        );

        if (!diffOfStartDate) {
          const diffOfDueDate = moment(a?.projectPhase?.dueDate).diff(
            moment(b?.projectPhase?.dueDate),
          );

          if (!diffOfDueDate) {
            return (
              a?.projectPhase?.projectPhaseId - b?.projectPhase?.projectPhaseId
            );
          }

          return diffOfDueDate;
        }

        return diffOfStartDate;
      }) ?? [],
    [data?.projectPhases],
  );

  const background = useGetBackground(widget);

  const filterMode: ArrElement<typeof dateFilters> = useMemo(() => {
    return (
      (runtimeConfig?.projectPlanFilter as ArrElement<typeof dateFilters>) ??
      'SPOTLIGHT'
    );
  }, [runtimeConfig?.projectPlanFilter]);

  const setFilterMode = useCallback(
    (mode: DateFilterType | 'SPOTLIGHT' | 'PLAN') => {
      onRuntimeConfigChange?.({
        ...(runtimeConfig ?? {}),
        projectPlanFilter: mode,
      });
    },
    [runtimeConfig, onRuntimeConfigChange],
  );

  const companyFilter = useMemo(() => {
    return runtimeConfig?.responsible;
  }, [runtimeConfig?.responsible]);

  const setCompanyFilter = useCallback(
    (companyId: number | undefined) => {
      onRuntimeConfigChange?.({
        ...(runtimeConfig ?? {}),
        responsible: companyId,
      });
    },
    [runtimeConfig, onRuntimeConfigChange],
  );

  useEffect(() => {
    if (!runtimeConfig?.projectPlanFilter) {
      onRuntimeConfigChange?.({
        ...(runtimeConfig ?? {}),
        projectPlanFilter: 'PLAN',
      });
    }
  }, [runtimeConfig, onRuntimeConfigChange]);

  return (
    <CpSectionWrapper
      isDisabled={disabled}
      className="rl-cp-project-plan-widget"
      background={background}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
    >
      <ProjectPlanTitle autoAdjustColor>
        {data?.title ?? (
          <FormattedMessage id="customer_portal.widgets.project_plan.title" />
        )}
      </ProjectPlanTitle>
      <Description autoAdjustColor>
        {data?.description ?? (
          <FormattedMessage id="customer_portal.widgets.project_plan.sub_title" />
        )}
      </Description>
      <FilterBar className="rl-cp-project-plan-filter cp-my-6">
        <FilterSetWrapper className="rl-cp-project-plan-date-filter md:cp-mb-0 cp-mb-[var(--cp-spacing-05)]">
          <Tag
            autoAdjustColor
            active={filterMode === 'SPOTLIGHT'}
            onClick={() => {
              setFilterMode('SPOTLIGHT');
            }}
          >
            <Sunny16 />
            <FormattedMessage id="customer_portal.widgets.project_plan.left_filters.SPOTLIGHT" />
          </Tag>

          <Tag
            autoAdjustColor
            active={filterMode === 'PLAN'}
            onClick={() => {
              setFilterMode('PLAN');
            }}
          >
            <FormattedMessage id="customer_portal.widgets.project_plan.left_filters.PLAN" />
          </Tag>

          <StyledPopover
            height="160px"
            getPopupContainer={() =>
              document.querySelector('#cp-modal-root') as HTMLElement
            }
            closeOnSelect
            placement="bottomLeft"
            trigger={['click']}
            content={
              <StyledMenu>
                {dateFilters.map((x) => (
                  <CpMenu.Item
                    key={x}
                    onClick={() => {
                      setFilterMode(x as DateFilterType);
                    }}
                    active={filterMode === x}
                  >
                    <FormattedMessage
                      id={`customer_portal.widgets.project_plan.left_filters.${x}`}
                    />
                  </CpMenu.Item>
                ))}
              </StyledMenu>
            }
          >
            <Tag autoAdjustColor active={dateFilters?.includes(filterMode)}>
              <FormattedMessage
                id={`customer_portal.widgets.project_plan.left_filters.${
                  dateFilters?.includes(filterMode) ? filterMode : 'OVERDUE'
                }`}
              />
              <ChevronDown16 />
            </Tag>
          </StyledPopover>
        </FilterSetWrapper>
        <Divider className="md:cp-hidden" />
        <CompanyFilterSetWrapper className="rl-cp-project-plan-company-filter md:cp-flex md:cp-justify-end cp-justify-start md:cp-mt-0 cp-mt-[var(--cp-spacing-05)]">
          <Tag
            autoAdjustColor
            active={!companyFilter}
            onClick={() => {
              setCompanyFilter(undefined);
            }}
          >
            <FormattedMessage id="all" />
          </Tag>
          {data?.customer?.companyId ? (
            <Tag
              autoAdjustColor
              active={data?.customer?.companyId === companyFilter}
              onClick={() => setCompanyFilter(data?.customer?.companyId)}
            >
              {data?.customer?.companyName}
            </Tag>
          ) : (
            <></>
          )}
          {data?.vendor?.companyId ? (
            <Tag
              autoAdjustColor
              active={data?.vendor?.companyId === companyFilter}
              onClick={() => setCompanyFilter(data?.vendor?.companyId)}
            >
              {data?.vendor?.companyName}
            </Tag>
          ) : (
            <></>
          )}
        </CompanyFilterSetWrapper>
      </FilterBar>
      {phases.map((p) => (
        <ProjectPlanPhase
          p={p}
          spotlightMap={spotlightMap}
          statusField={data?.statusField}
          refetch={() =>
            onRuntimeConfigChange?.(_.cloneDeep(runtimeConfig ?? {}))
          }
        />
      ))}
    </CpSectionWrapper>
  );
};
